import { Box, Grid2, Typography } from '@mui/material'
import type { Product, ProductPerformance } from 'api/inventory'
import Add from 'components/icons/Add'
import { useProductPerformance } from 'hooks/useInventory'
import { artImageFor } from 'hooks/useProducts'
import { useState } from 'react'
import { formatShareOfPainting } from 'utils/formatShareOfPainting'
import { formattedPrice } from 'utils/util'
import PercentageReturn from './PercentageReturn'
import useStyles from './styles'

interface ArtVaultProductProps {
  // The catalog's product, or the holding's own (GET /products leaves the
  // painting out while art_asset_web is off; the performance summaries never
  // leave out a product the customer holds).
  product: Product
  productPerformance: Partial<ProductPerformance>
  reversed?: boolean
  vaultId?: number
}

/**
 * The painting's row in My Vault. The metals' row prints "<NAME> BULLION" over
 * a bar photograph and grams, so the painting read "C.F. GOLDIE I BULLION"
 * beside a broken image. Here it has its name as the backend gives it, its own
 * picture, and the value first with the share of the painting underneath, the
 * layout of the metal rows (goldie-ui #684).
 *
 * No TRADE, INSIGHT or film: those open the metals' drawers (per gram prices,
 * "99.99% FINE", a sale the backend refuses for the painting, which sells only
 * by express sell), and the website has no painting page yet.
 *
 * goldie-cloud's platinum-dashboard e2e leaves this row out of its metals-only
 * picture with `div:has(> div > img[alt="C.F. Goldie I"])`: keep the image a
 * child of the first box in the row, with the product's name as its alt. That
 * rule finds the row only while its picture is on the page (one that fails to
 * load is removed) and only under this name, so the row also carries
 * data-testid='vault-art-row', a hook that holds either way.
 */
export default function ArtVaultProduct({
  product,
  productPerformance,
  reversed = false,
  vaultId
}: Readonly<ArtVaultProductProps>): JSX.Element {
  const { classes } = useStyles()
  const [imageFailed, setImageFailed] = useState(false)

  const { data } = useProductPerformance(
    product.id ? Number(product.id) : 0,
    vaultId
  )

  const image = artImageFor(product)

  return (
    <Grid2
      container
      className={classes.aboveTheFoldContainer}
      direction={reversed ? 'row-reverse' : 'row'}
      data-testid='vault-art-row'
    >
      <Box
        flex={4}
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
      >
        {/* No picture, or one that fails to load: an empty space the size of
            the metals' pictures, never a broken image. */}
        {image && !imageFailed && (
          <img
            src={image}
            alt={product.name}
            className={classes.artImage}
            onError={() => setImageFailed(true)}
          />
        )}
      </Box>
      <Box
        flex={6}
        display='flex'
        flexDirection={reversed ? 'row-reverse' : 'row'}
        alignItems='center'
      >
        <Box mt={1}>
          <Add />
        </Box>
        <Box>
          <Box
            display='flex'
            alignItems='center'
            flexDirection={reversed ? 'row-reverse' : 'row'}
            gap={0.5}
          >
            {/* The name as the backend gives it ("C.F. Goldie I"), not
                upper-cased like the metals' "GOLD BULLION". */}
            <Box
              whiteSpace='nowrap'
              display='flex'
              textAlign={reversed ? 'right' : 'left'}
            >
              <Typography>{product.name}</Typography>
              <PercentageReturn
                percentageReturn={
                  productPerformance.totalReturnAsPercentage ?? 0
                }
              />
            </Box>
          </Box>

          {/* Value first, the share underneath: a share of the painting, never
              grams (goldie-cloud services/product_kind.py). */}
          <Box
            display='flex'
            flexDirection='column'
            alignItems={reversed ? 'flex-end' : 'flex-start'}
          >
            <Typography whiteSpace='nowrap'>
              <Typography component='span' color='text.secondary'>
                Value:
              </Typography>{' '}
              <Typography component='span' color='primary'>
                ${formattedPrice(Number(data?.currentValue ?? 0))}
              </Typography>
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              whiteSpace='nowrap'
            >
              Own:{' '}
              {formatShareOfPainting(
                Number(data?.quantityOwned ?? 0),
                product.quantityPerItem
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Grid2>
  )
}
